import { useState, useEffect } from "react";
import api from "../../../services/api";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuthenticate from "../../../hooks/useAuthenticate";

import Input from "../../../components/atoms/input";
import Loading from "../../../components/atoms/loading";
import NavBar from "../../../components/organisms/navbar";
import Button from "../../../components/atoms/button";

import { FormContainer } from "../../enrollment/styles";
import { FormGroup, Content, PageTitle } from "./styles";

interface FederationConfig {
  id: number;
  slug: string;
  name: string;
}

interface TournamentFormData {
  name: string;
  descriptions: string;
  location: string;
  date: string;
  status: boolean;
  priceCombo?: number;
  slug?: string;
  rules?: FileList;
  isFederated?: boolean;
  federationSlug?: string;
  requireAffiliation?: boolean;
}

interface TournamentPayload {
  name: string;
  descriptions: string;
  location: string;
  date: string;
  status: boolean;
  priceCombo?: number;
  slug?: string;
  rules?: object;
  isFederated?: boolean;
  federationSlug?: string;
  requireAffiliation?: boolean;
}

const NewTournament = () => {
  const navigate = useNavigate();
  const { getToken } = useAuthenticate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TournamentFormData>();

  const [disabled, setDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [federationConfigs, setFederationConfigs] = useState<FederationConfig[]>([]);

  const rulesFile = watch("rules");
  const isFederated = watch("isFederated");

  useEffect(() => {
    const fetchFederationConfigs = async () => {
      try {
        const response = await api.get("/managers/federation-configs", {
          headers: { Authorization: `Bearer ${getToken() || ""}` },
        });
        setFederationConfigs(response.data);
      } catch (error) {
        console.error("Erro ao buscar federacoes:", error);
      }
    };
    fetchFederationConfigs();
  }, []);

  const onSubmit = async (data: TournamentFormData) => {
    setDisabled(true);
    setLoading(true);

    let rulesJson: object | undefined = undefined;

    if (data.rules && data.rules[0]) {
      const file = data.rules[0];
      if (file.type !== 'application/json') {
        toast.error("O arquivo de regras deve ser um JSON válido.");
        setDisabled(false);
        setLoading(false);
        return;
      }

      const reader = new FileReader();
      const fileReadPromise = new Promise<string>((resolve, reject) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      try {
        const fileContent = await fileReadPromise;
        rulesJson = JSON.parse(fileContent);
        console.log("File content parsed to JSON:", rulesJson);
      } catch (fileError) {
        console.error("Error parsing rules file as JSON:", fileError);
        toast.error("Erro ao ler ou parsear o arquivo de regras como JSON.");
        setDisabled(false);
        setLoading(false);
        return;
      }
    }

    const payload: TournamentPayload = {
      name: data.name,
      descriptions: data.descriptions,
      location: data.location,
      date: data.date,
      status: data.status,
    };

    if (data.priceCombo !== undefined && data.priceCombo !== null) {
      payload.priceCombo = data.priceCombo;
    }

    if (data.slug !== undefined && data.slug !== null) {
      payload.slug = data.slug;
    }

    if (rulesJson) {
      payload.rules = rulesJson;
    }

    if (data.isFederated) {
      payload.isFederated = true;
      payload.federationSlug = data.federationSlug;
      payload.requireAffiliation = data.requireAffiliation || false;
    }

    try {
      await api.post("/managers/tournaments", payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      toast.success("Torneio foi cadastrado com sucesso!");
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error: any) {
      console.error("Erro ao cadastrar torneio:", error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(`Erro: ${error.response.data.message}`);
      } else {
        toast.error("Não foi possível o cadastro do torneio!");
      }
    } finally {
      setDisabled(false);
      setLoading(false);
    }
  };

  if (loading)
    return <Loading text="Aguarde, estamos processando seu cadastro." />;

  return (
    <>
      <NavBar />
      <Content>
        <PageTitle>Cadastro Evento</PageTitle>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Input
              label="Nome do Evento"
              name="name"
              type="text"
              register={register}
              required
              hasError={!!errors.name}
              error={errors.name?.message || "Este campo é obrigatório"}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Descrição do Evento"
              name="descriptions"
              type="textarea"
              register={register}
              required
              hasError={!!errors.descriptions}
              error={errors.descriptions?.message || "Este campo é obrigatório"}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Local"
              name="location"
              type="text"
              register={register}
              required
              hasError={!!errors.location}
              error={errors.location?.message || "Este campo é obrigatório"}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Data do Evento"
              name="date"
              placeholder={`10 de agosto de ${new Date().getFullYear()}`}
              type="date"
              register={register}
              required
              hasError={!!errors.date}
              error={errors.date?.message || "Este campo é obrigatório"}
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Slug do torneio (Opcional)"
              name="slug"
              type="text"
              register={register}
              hasError={!!errors.slug}
              error={errors.slug?.message || "Deve ser uma string"}
              required={undefined}
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Preço Combo (Opcional)"
              name="priceCombo"
              type="number"
              register={register}
              hasError={!!errors.priceCombo}
              error={errors.priceCombo?.message || "Deve ser um número"}
              required={undefined}
            />
          </FormGroup>

          <FormGroup>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                {...register("isFederated")}
              />
              Torneio Federado
            </label>
          </FormGroup>

          {isFederated && (
            <>
              <FormGroup>
                <label htmlFor="federationSlug">Federacao</label>
                <select
                  id="federationSlug"
                  {...register("federationSlug", { required: isFederated ? "Selecione uma federacao" : false })}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "8px",
                    marginTop: "5px",
                    marginBottom: "10px",
                    border: `1px solid ${errors.federationSlug ? "red" : "#ccc"}`,
                    borderRadius: "4px",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">Selecione uma federacao...</option>
                  {federationConfigs.map((fc) => (
                    <option key={fc.id} value={fc.slug}>
                      {fc.name}
                    </option>
                  ))}
                </select>
                {errors.federationSlug && (
                  <span style={{ color: "red", fontSize: "0.8em" }}>
                    {errors.federationSlug.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    {...register("requireAffiliation")}
                  />
                  Exigir filiacao ativa
                </label>
              </FormGroup>
            </>
          )}

          <FormGroup>
            <label htmlFor="rules">Arquivo de Regras (JSON - Opcional)</label>
            <input
              id="rules"
              type="file"
              {...register("rules")}
              accept="application/json"
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                marginTop: '5px',
                marginBottom: '10px',
                border: `1px solid ${errors.rules ? 'red' : '#ccc'}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
            {rulesFile?.[0] && <p style={{ fontSize: '0.9em', color: '#666' }}>Arquivo selecionado: {rulesFile[0].name}</p>}
            {errors.rules && (
              <span style={{ color: 'red', fontSize: '0.8em' }}>
                {errors.rules.message || "Selecione um arquivo JSON válido"}
              </span>
            )}
          </FormGroup>

          <Button type="submit" label="Cadastrar Evento" disabled={disabled} />
        </FormContainer>
      </Content>
    </>
  );
};

export default NewTournament;